import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { apiJson } from '../lib/api'
import { Failed, PendingButton, SkeletonRows } from '../components/pending'
import { PageHeader, PageBody } from '../components/PageHeader'
import styles from './IntegrationsPage.module.css'
import own from './ApiKeysPage.module.css'

// The workspace's Dataphyll Field devices (routes/field-devices.js, migration 181). A tablet is registered from its own
// screen: it shows a QR, someone who may add Sites & jobs scans it and signs in (DevicePairPage). Here it is renamed,
// or switched off, which locks it out at once; switching it on again lets it back in with the same token.

export interface FieldDevice {
  id: string
  device_id: string
  name: string | null
  device_type: string | null
  form_factor: 'tablet' | 'phone' | null
  model: string | null
  platform: string | null
  app_version: string | null
  active: boolean
  registered_at: string | null
  registered_by_email: string | null
  last_seen_at: string | null
  deactivated_at: string | null
}

const APP_LABEL: Record<string, string> = {
  field_station: 'Weigh station',
  preassign: 'Preassign station',
  supervisor: 'Supervisor',
}

export const deviceAppLabel = (t: string | null) => (t && APP_LABEL[t]) || 'Weigh station'

const PLATFORM_LABEL: Record<string, string> = { android: 'Android', ios: 'iPhone / iPad', web: 'Web' }

export const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'

const errText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.')

export function DevicesPage() {
  const { user } = useAuth()
  const [devices, setDevices] = useState<FieldDevice[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [failed, setFailed] = useState<{ at: string; text: string } | null>(null)
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      setDevices((await apiJson<{ devices: FieldDevice[] }>(user, '/api/devices')).devices)
    } catch (err) {
      setLoadError(errText(err))
    }
  }, [user])

  useEffect(() => {
    load()
  }, [load])

  const change = async (d: FieldDevice, body: { name?: string; active?: boolean }) => {
    setBusy(d.id)
    setFailed(null)
    try {
      const r = await apiJson<{ device: FieldDevice }>(user, `/api/devices/${d.id}`, { method: 'PATCH', body })
      setDevices((list) => (list || []).map((x) => (x.id === d.id ? r.device : x)))
      setRenaming(null)
    } catch (err) {
      setFailed({ at: d.id, text: errText(err) })
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader title="Devices" />
      <PageBody>
        <div className={own.notice} data-testid="devices-notice">
          <p>
            <strong>Dataphyll Field tablets and phones registered to this workspace.</strong> To add one, open Dataphyll Field on it and scan the QR
            it shows. Then sign in, choose this workspace and name the device.
          </p>
          <p>Switching a device off locks it out straight away. Its records stay. Switch it on again to let it back in.</p>
        </div>

        {loadError ? (
          <div className={styles.error} role="alert">
            <p>{loadError}</p>
            <button onClick={load}>Retry</button>
          </div>
        ) : (
          <section className={own.list} aria-label="Devices">
            <h2 className={own.listHead}>Registered devices</h2>
            {devices === null ? (
              <SkeletonRows rowClass={own.row} cols={5} rows={3} label="Loading devices" />
            ) : devices.length === 0 ? (
              <div className={styles.empty}>
                <p>No devices yet. Open Dataphyll Field on a tablet and scan its QR to register it here.</p>
              </div>
            ) : (
              <div className={own.tableCard}>
                <table className={own.table} data-testid="devices">
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Used as</th>
                      <th>Version</th>
                      <th>Registered</th>
                      <th>Last seen</th>
                      <th>State</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {devices.map((d) => (
                      <tr key={d.id} data-testid="device-row" className={d.active ? undefined : own.revokedRow}>
                        <td>
                          {renaming?.id === d.id ? (
                            <form
                              className={styles.keyRow}
                              onSubmit={(e) => {
                                e.preventDefault()
                                if (renaming.name.trim()) change(d, { name: renaming.name.trim() })
                              }}
                            >
                              <input
                                className={styles.input}
                                value={renaming.name}
                                maxLength={80}
                                autoFocus
                                aria-label="Device name"
                                onChange={(e) => setRenaming({ id: d.id, name: e.target.value })}
                              />
                              <PendingButton type="submit" className={styles.primary} busy={busy === d.id} pending="Saving…" disabled={!renaming.name.trim() || !!busy}>
                                Save
                              </PendingButton>
                              <button type="button" className={styles.button} disabled={busy === d.id} onClick={() => setRenaming(null)}>
                                Cancel
                              </button>
                            </form>
                          ) : (
                            <>
                              {d.name || d.device_id}
                              <div className={own.sub}>
                                <code>{d.device_id}</code>
                                {d.model ? ` · ${d.model}` : ''}
                              </div>
                            </>
                          )}
                        </td>
                        <td>
                          {deviceAppLabel(d.device_type)}
                          {d.form_factor && <div className={own.sub}>{d.form_factor === 'phone' ? 'Phone' : 'Tablet'}</div>}
                        </td>
                        <td>
                          {d.app_version || '—'}
                          {d.platform && PLATFORM_LABEL[d.platform] && <div className={own.sub}>{PLATFORM_LABEL[d.platform]}</div>}
                        </td>
                        <td>
                          {when(d.registered_at)}
                          {d.registered_by_email && <div className={own.sub}>by {d.registered_by_email}</div>}
                        </td>
                        <td>{when(d.last_seen_at)}</td>
                        <td>
                          {d.active ? (
                            <span className={`${styles.status} ${styles.pillGood}`}>On</span>
                          ) : (
                            <>
                              <span className={`${styles.status} ${styles.pillMuted}`}>Off</span>
                              {d.deactivated_at && <div className={own.sub}>since {when(d.deactivated_at)}</div>}
                            </>
                          )}
                        </td>
                        <td className={own.actions}>
                          {renaming?.id !== d.id && (
                            <>
                              <button type="button" className={styles.button} disabled={!!busy} onClick={() => setRenaming({ id: d.id, name: d.name || d.device_id })}>
                                Rename
                              </button>{' '}
                              <PendingButton
                                className={d.active ? own.revokeBtn : styles.button}
                                busy={busy === d.id}
                                pending={d.active ? 'Switching off…' : 'Switching on…'}
                                disabled={!!busy}
                                onClick={() => change(d, { active: !d.active })}
                              >
                                {d.active ? 'Switch off' : 'Switch on'}
                              </PendingButton>
                            </>
                          )}
                          {failed?.at === d.id && <Failed text={failed.text} />}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </PageBody>
    </>
  )
}
